// Generación de los carteles en PDF con pdf-lib, sin React. La usan CartelesPage (la
// descarga) y su vista previa (los mismos textos). Es el dibujo del generador viejo de
// carteles, sin cambios: mismo orden de fuentes, mismas cuentas, mismos colores (Ficha 7).
import { PDFDocument, rgb, StandardFonts, type PDFFont } from "pdf-lib";
import { DateTime } from "luxon";
import type { Pharmacy, Shift } from "../../types";
import type { Orientacion } from "./plantillasApi";

export interface PDFPreset {
  // Fonts
  dayFont: string;
  dayFontSize: number;
  dayFontBold: boolean;

  nameFont: string;
  nameFontSize: number;
  nameFontBold: boolean;

  infoFont: string;
  infoFontSize: number;
  infoFontBold: boolean;

  // Layout positions (in points)
  colWidth: number;
  startX: number;
  colGap: number;

  // Header Blue Box coordinates
  headerBoxBottom: number;
  headerBoxHeight: number;

  // Pharmacy Green Box coordinates
  pharmacyBoxBottom: number;
  pharmacyBoxHeight: number;

  // Spacing inside pharmacy box
  lineGap: number;
  pharmacyGap: number;

  vStartX: number;
  vColWidth: number;
  vColGap: number;
  vHeaderBoxBottomR1: number;
  vHeaderBoxBottomR2: number;
  vHeaderBoxHeight: number;
  vPharmacyBoxBottomR1: number;
  vPharmacyBoxBottomR2: number;
  vPharmacyBoxHeight: number;
}

export const DEFAULT_PRESET: PDFPreset = {
  dayFont: "rounded",
  dayFontSize: 22,
  dayFontBold: true,

  nameFont: "helvetica",
  nameFontSize: 14,
  nameFontBold: true,

  infoFont: "helvetica",
  infoFontSize: 13,
  infoFontBold: false,

  // Medidas de horizontal-limpio.pdf y «vertical - limpio.pdf» (recuadros vectoriales, §5)
  colWidth: 181.1,
  startX: 40.5,
  colGap: 12.1,

  headerBoxBottom: 355.5,
  headerBoxHeight: 44.6,

  pharmacyBoxBottom: 134,
  pharmacyBoxHeight: 211.7,

  lineGap: 3,
  pharmacyGap: 9,

  vStartX: 114.7,
  vColWidth: 177,
  vColGap: 11.9,
  vHeaderBoxBottomR1: 630.1,
  vHeaderBoxBottomR2: 361.2,
  vHeaderBoxHeight: 43.6,
  vPharmacyBoxBottomR1: 413.6,
  vPharmacyBoxBottomR2: 144.7,
  vPharmacyBoxHeight: 207
};

interface ScaledPresetValues {
  nameFontSize: number;
  infoFontSize: number;
  lineGap: number;
  pharmacyGap: number;
}

function getScaledPresetValues(dayPharmacies: Pharmacy[], preset: PDFPreset, boxHeight: number): ScaledPresetValues {
  const { nameFontSize, infoFontSize, lineGap, pharmacyGap } = preset;

  let actualNameFontSize = nameFontSize;
  let actualInfoFontSize = infoFontSize;
  let actualLineGap = lineGap;
  let actualPharmacyGap = pharmacyGap;

  let pharmacyHeights = dayPharmacies.map(pharm => {
    const hasPhone = !!pharm.telefono;
    return actualNameFontSize + actualLineGap + actualInfoFontSize + (hasPhone ? actualLineGap + actualInfoFontSize : 0);
  });
  let totalHeightsSum = pharmacyHeights.reduce((sum, h) => sum + h, 0);
  let totalGapsSum = (dayPharmacies.length - 1) * actualPharmacyGap;
  let totalBlockHeight = totalHeightsSum + totalGapsSum;

  const maxAllowedHeight = boxHeight - 12;
  if (totalBlockHeight > maxAllowedHeight) {
    const scale = maxAllowedHeight / totalBlockHeight;
    actualNameFontSize = Math.max(10, Math.floor(nameFontSize * scale));
    actualInfoFontSize = Math.max(9, Math.floor(infoFontSize * scale));
    actualLineGap = Math.max(2, Math.floor(lineGap * scale));
    actualPharmacyGap = Math.max(6, Math.floor(pharmacyGap * scale));
  }

  return {
    nameFontSize: actualNameFontSize,
    infoFontSize: actualInfoFontSize,
    lineGap: actualLineGap,
    pharmacyGap: actualPharmacyGap
  };
}

/**
 * Primer día (yyyy-MM-dd) de cada hoja del mes «yyyy-MM»: arranca el viernes anterior
 * (o igual) al día 1 y alterna saltos de 4 y 3 días, hasta pasar el fin de mes.
 */
export function armarHojas(mes: string): string[] {
  const startOfMonth = DateTime.fromISO(`${mes}-01`);
  if (!startOfMonth.isValid) return [];
  const hojas: string[] = [];
  let currentStart = startOfMonth;
  while (currentStart.weekday !== 5) {
    currentStart = currentStart.minus({ days: 1 });
  }
  let isFridayStart = true;
  while (true) {
    hojas.push(currentStart.toFormat("yyyy-MM-dd"));
    if (isFridayStart) {
      currentStart = currentStart.plus({ days: 4 });
    } else {
      currentStart = currentStart.plus({ days: 3 });
    }
    isFridayStart = !isFridayStart;
    if (currentStart.month !== startOfMonth.month && currentStart > startOfMonth.endOf("month")) {
      break;
    }
  }
  return hojas;
}

/** Los cuatro días que muestra una hoja. */
export function diasDeHoja(inicio: string): DateTime[] {
  const startDt = DateTime.fromISO(inicio);
  return [0, 1, 2, 3].map(i => startDt.plus({ days: i }));
}

/** El encabezado de cada columna: «Viernes 30». */
export function etiquetaDia(dt: DateTime): string {
  const trimmedDayStr = dt.setLocale("es").toFormat("cccc d").trim();
  return trimmedDayStr.charAt(0).toUpperCase() + trimmedDayStr.slice(1);
}

export function farmaciasDelDia(fecha: string, shifts: Shift[], pharmacies: Pharmacy[]): Pharmacy[] {
  const shift = shifts.find(s => s.date === fecha);
  return shift
    ? (shift.pharmacyIds.map(id => pharmacies.find(p => p.id === id)).filter(Boolean) as Pharmacy[])
    : [];
}

/** El nombre tal como sale impreso. */
export function nombreEnCartel(pharm: Pharmacy): string {
  return pharm.nombre.trim().toUpperCase();
}

async function getPDFFont(pdfDoc: PDFDocument, fontKey: string, isBold: boolean): Promise<PDFFont> {
  switch (fontKey) {
    case "times":
      return await pdfDoc.embedFont(isBold ? StandardFonts.TimesRomanBold : StandardFonts.TimesRoman);
    case "courier":
      return await pdfDoc.embedFont(isBold ? StandardFonts.CourierBold : StandardFonts.Courier);
    case "rounded":
    case "helvetica":
    default:
      return await pdfDoc.embedFont(isBold ? StandardFonts.HelveticaBold : StandardFonts.Helvetica);
  }
}

export interface DatosCartel {
  hojas: string[]; // primer día de cada hoja elegida, en orden
  preset: PDFPreset;
  shifts: Shift[];
  pharmacies: Pharmacy[];
}

/** Un PDF con una página por hoja, dibujada sobre la primera página de la plantilla. */
export async function generarCartel(plantilla: ArrayBuffer, orientacion: Orientacion, datos: DatosCartel): Promise<Uint8Array> {
  const isVertical = orientacion === "vertical";
  const { hojas, preset, shifts, pharmacies } = datos;

  const templatePdfDoc = await PDFDocument.load(plantilla);
  const outPdfDoc = await PDFDocument.create();

  const {
    dayFont, dayFontSize, dayFontBold,
    nameFont, nameFontBold,
    infoFont, infoFontBold,
    colWidth, startX, colGap,
    headerBoxBottom, headerBoxHeight,
    pharmacyBoxBottom, pharmacyBoxHeight,
    vStartX, vColWidth, vColGap,
    vHeaderBoxBottomR1, vHeaderBoxBottomR2, vHeaderBoxHeight,
    vPharmacyBoxBottomR1, vPharmacyBoxBottomR2, vPharmacyBoxHeight
  } = preset;

  const embeddedFonts: Record<string, PDFFont> = {};
  const getCachedPDFFont = async (fontKey: string, isBold: boolean) => {
    const cacheKey = `${fontKey}_${isBold ? "bold" : "regular"}`;
    if (embeddedFonts[cacheKey]) {
      return embeddedFonts[cacheKey];
    }
    const font = await getPDFFont(outPdfDoc, fontKey, isBold);
    embeddedFonts[cacheKey] = font;
    return font;
  };

  // P-25: la plantilla se embebe una sola vez y cada hoja la dibuja (antes se copiaba entera en cada hoja)
  const [plantillaEmbebida] = await outPdfDoc.embedPdf(templatePdfDoc, [0]);

  for (const blockStartDateStr of hojas) {
    const startDt = DateTime.fromISO(blockStartDateStr);

    const copiedPage = outPdfDoc.addPage([plantillaEmbebida.width, plantillaEmbebida.height]);
    copiedPage.drawPage(plantillaEmbebida, { x: 0, y: 0 });

    // §5: la letra es pareja en toda la hoja: manda el día que más necesita achicar
    const boxHeightHoja = isVertical ? vPharmacyBoxHeight : pharmacyBoxHeight;
    const escalaHoja = [0, 1, 2, 3]
      .map(d => farmaciasDelDia(startDt.plus({ days: d }).toFormat("yyyy-MM-dd"), shifts, pharmacies))
      .filter(lista => lista.length > 0)
      .map(lista => getScaledPresetValues(lista, preset, boxHeightHoja))
      .reduce<ScaledPresetValues | null>((min, v) => min ? {
        nameFontSize: Math.min(min.nameFontSize, v.nameFontSize),
        infoFontSize: Math.min(min.infoFontSize, v.infoFontSize),
        lineGap: Math.min(min.lineGap, v.lineGap),
        pharmacyGap: Math.min(min.pharmacyGap, v.pharmacyGap)
      } : v, null);

    for (let i = 0; i < 4; i++) {
      const dt = startDt.plus({ days: i });
      const capitalizedDayStr = etiquetaDia(dt);
      const dayPharmacies = farmaciasDelDia(dt.toFormat("yyyy-MM-dd"), shifts, pharmacies);

      let centerColX;
      let headerCenterY;
      let pharmacyCenterYBase;
      let actualPharmacyBoxHeight = pharmacyBoxHeight;

      if (isVertical) {
        const vCol = i % 2;
        const vRow = Math.floor(i / 2);
        centerColX = vStartX + vCol * (vColWidth + vColGap) + (vColWidth / 2);
        headerCenterY = (vRow === 0 ? vHeaderBoxBottomR1 : vHeaderBoxBottomR2) + (vHeaderBoxHeight / 2);
        pharmacyCenterYBase = (vRow === 0 ? vPharmacyBoxBottomR1 : vPharmacyBoxBottomR2);
        actualPharmacyBoxHeight = vPharmacyBoxHeight;
      } else {
        const xPos = startX + i * (colWidth + colGap);
        centerColX = xPos + (colWidth / 2);
        headerCenterY = headerBoxBottom + headerBoxHeight / 2;
        pharmacyCenterYBase = pharmacyBoxBottom;
      }

      const dayFontEmbed = await getCachedPDFFont(dayFont, dayFontBold);
      const textWidth = dayFontEmbed.widthOfTextAtSize(capitalizedDayStr, dayFontSize);

      const dayTextY = headerCenterY - (dayFontSize * 0.7) / 2;

      copiedPage.drawText(capitalizedDayStr, {
        x: centerColX - (textWidth / 2),
        y: dayTextY,
        size: dayFontSize,
        font: dayFontEmbed,
        color: rgb(1, 1, 1),
      });

      if (dayPharmacies.length > 0) {
        const nameFontEmbed = await getCachedPDFFont(nameFont, nameFontBold);
        const infoFontEmbed = await getCachedPDFFont(infoFont, infoFontBold);

        const {
          nameFontSize: actualNameFontSize,
          infoFontSize: actualInfoFontSize,
          lineGap: actualLineGap,
          pharmacyGap: actualPharmacyGap
        } = escalaHoja ?? getScaledPresetValues(dayPharmacies, preset, actualPharmacyBoxHeight);

        const pharmacyHeights = dayPharmacies.map(pharm => {
          const hasPhone = !!pharm.telefono;
          return actualNameFontSize + actualLineGap + actualInfoFontSize + (hasPhone ? actualLineGap + actualInfoFontSize : 0);
        });
        const totalHeightsSum = pharmacyHeights.reduce((sum, h) => sum + h, 0);
        const totalGapsSum = (dayPharmacies.length - 1) * actualPharmacyGap;
        const totalBlockHeight = totalHeightsSum + totalGapsSum;

        const pharmacyCenterY = pharmacyCenterYBase + actualPharmacyBoxHeight / 2;
        const blockTopY = pharmacyCenterY + totalBlockHeight / 2;

        let currentY = blockTopY;

        for (let j = 0; j < dayPharmacies.length; j++) {
          const pharm = dayPharmacies[j];
          const hasPhone = !!pharm.telefono;

          const name = nombreEnCartel(pharm);
          const nameWidth = nameFontEmbed.widthOfTextAtSize(name, actualNameFontSize);
          copiedPage.drawText(name, {
            x: centerColX - (nameWidth / 2),
            y: currentY - actualNameFontSize,
            size: actualNameFontSize,
            font: nameFontEmbed,
            color: rgb(0, 0, 0),
          });
          currentY -= actualNameFontSize + actualLineGap;

          const address = pharm.direccion.trim();
          const addrWidth = infoFontEmbed.widthOfTextAtSize(address, actualInfoFontSize);
          copiedPage.drawText(address, {
            x: centerColX - (addrWidth / 2),
            y: currentY - actualInfoFontSize,
            size: actualInfoFontSize,
            font: infoFontEmbed,
            color: rgb(0.15, 0.15, 0.15),
          });
          currentY -= actualInfoFontSize;

          if (hasPhone && pharm.telefono) {
            currentY -= actualLineGap;
            const phone = pharm.telefono.trim();
            const phoneWidth = infoFontEmbed.widthOfTextAtSize(phone, actualInfoFontSize);
            copiedPage.drawText(phone, {
              x: centerColX - (phoneWidth / 2),
              y: currentY - actualInfoFontSize,
              size: actualInfoFontSize,
              font: infoFontEmbed,
              color: rgb(0.15, 0.15, 0.15),
            });
            currentY -= actualInfoFontSize;
          }
          currentY -= actualPharmacyGap;
        }
      }
    }
  }

  return outPdfDoc.save();
}

/** Los dos carteles con la misma lista de hojas: primero el horizontal, después el vertical. */
export async function generarCarteles(
  plantillas: Record<Orientacion, ArrayBuffer>,
  datos: DatosCartel
): Promise<Record<Orientacion, Uint8Array>> {
  const horizontal = await generarCartel(plantillas.horizontal, "horizontal", datos);
  const vertical = await generarCartel(plantillas.vertical, "vertical", datos);
  return { horizontal, vertical };
}
